/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import { BadgeCheck, RefreshCw, Search, PencilRuler, HardHat, CheckCircle2, Users, ChevronRight, AlertCircle, FolderInput, Loader2 } from "lucide-react";
import { fmtMoney } from "../lib/appUtils";
import { fmtD } from "../lib/date";
import { CategoryBadge, DueBadge, SERVICE_LABEL } from "../components/crm/SignedBits";
import CRMSignedCustomerHome from "./CRMSignedCustomerHome";
import { CrmDetailDrawer } from "../components/crm/CrmDetailDrawer";
import { SignedBulkCreateDialog } from "../components/crm/SignedBulkCreateDialog";
import { CompletedCreateDialog } from "../components/crm/CompletedCreateDialog";
import { SignedOverdueDigestCard } from "../components/crm/SignedOverdueDigestCard";
import { SignedUnreadDigestCard } from "../components/crm/SignedUnreadDigestCard";
import { BatchCollectCard } from "../components/finance/BatchCollectCard";
import { EmptyState, TableSkeleton } from "../components/States";
import { SignedQuickFilters, SIGNED_FILTER_KEYS } from "../components/crm/SignedQuickFilters";
import { QuickViews } from "../components/QuickViews";
import { createdRange, tzDateStr } from "../components/crm/CreatedFilter";

const CATS = [
  { key: "all", icon: Users, zh: "全部已签约", en: "All signed", tint: null },
  { key: "design", icon: PencilRuler, zh: "设计阶段", en: "Design", tint: "#4f46e5" },
  { key: "construction", icon: HardHat, zh: "施工阶段", en: "Construction", tint: "#d97706" },
  { key: "completed", icon: CheckCircle2, zh: "已完工", en: "Completed", tint: "#059669" },
];

const CountCard = ({ c, n, on, zh, onClick }) => {
  const Icon = c.icon;
  return (
    <button type="button" data-testid={`signed-count-${c.key}`} data-on={on ? 1 : 0} onClick={onClick}
      className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-left rounded-xl border p-4 transition-all ${on ? "border-brand-500 ring-2 ring-brand-500/30 bg-white shadow-sm" : "bg-white border-slate-200 hover:border-slate-400"}`)}>
      <div className="flex-wrap md:flex-nowrap flex items-center justify-between">
        <p className="text-xs text-slate-400">{zh ? c.zh : c.en}</p>
        <Icon className={`h-4 w-4 ${c.tint ? "" : "text-slate-700"}`} style={c.tint ? { color: c.tint } : undefined} />
      </div>
      <p className={`text-3xl font-bold mt-1 tabular-nums ${c.tint ? "" : "text-slate-900"}`} style={c.tint ? { color: c.tint } : undefined}>{n ?? "–"}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{on ? (zh ? "已筛选 · 再点取消" : "Filtered · click to clear") : (zh ? "点击筛选" : "Click to filter")}</p>
    </button>
  );
};

const PAGE = 20;          // 每页条数（弱网：首屏只拉 20 条，滚动再加载）
const PREFETCH_PX = 200;  // 距底部多少像素开始预加载下一页（过大会让首屏直接连拉两页）
const THROTTLE_MS = 400;  // 滚动触底回调节流

export default function CRMSignedCustomers() {
  const { lang, user } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const dueOnly = params.get("due") === "1";
  // 抽屉模式（需求 2026-10-13）：路由 /crm/signed/:id 也渲染本组件，:id 存在即代表「要叠出已签约客户详情抽屉」。
  // 本页的筛选状态存在 URL search（cat / due / SIGNED_FILTER_KEYS），所以打开抽屉时必须把当前 search
  // 一并带进详情 URL，关闭时再原样带回 —— 否则「关掉抽屉」会把用户的筛选条件清空。
  const { id: signedId } = useParams();
  const openSigned = (leadId, extra) => {
    const n = new URLSearchParams(params);
    if (extra) Object.entries(extra).forEach(([k, v]) => n.set(k, v));
    const s = n.toString();
    nav(`/crm/signed/${leadId}${s ? `?${s}` : ""}`);
  };
  // 关闭抽屉：回到列表并还原筛选；顺手丢掉只对详情有意义的 collect（立刻收款）参数，避免污染列表 URL。
  const closeSigned = () => {
    const n = new URLSearchParams(params); n.delete("collect");
    const s = n.toString();
    nav(`/crm/signed${s ? `?${s}` : ""}`);
  };
  const fkey = SIGNED_FILTER_KEYS.map((k) => params.get(k) || "").join("|");
  const [data, setData] = useState(null);       // counts / totals / scope（全量，首页返回）
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [q, setQ] = useState(params.get("q") || "");
  const [qd, setQd] = useState(q);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(false);
  const canBulk = ["admin", "ceo"].includes(user?.role);
  const isMgr = ["admin", "ceo", "sales_vp", "sales_manager", "marketing_manager"].includes(user?.role);
  const sentinelRef = useRef(null);
  const inflight = useRef(null);      // 当前请求的 AbortController（同一时间只允许一个请求）
  const reqSeq = useRef(0);           // 请求序号：筛选切换后丢弃过期响应
  const lastTrigger = useRef(0);      // 触底节流时间戳
  const cursor = useRef({ offset: 0, hasMore: false });

  useEffect(() => { const t = setTimeout(() => setQd(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const fetchPage = useCallback(async (offset, { fresh = false } = {}) => {
    if (inflight.current) inflight.current.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    const seq = ++reqSeq.current;
    const first = offset === 0;
    first ? setLoading(true) : setLoadingMore(true);
    setLoadError("");
    try {
      const p = { offset, limit: PAGE };
      if (qd) p.q = qd;
      if (cat !== "all") p.category = cat;
      if (dueOnly) p.due = 1;
      if (fresh) p.fresh = 1;
      const fv = Object.fromEntries(SIGNED_FILTER_KEYS.map((k, i) => [k, fkey.split("|")[i] || ""]));
      // 施工组交给服务端在分页前筛选，列表、顶部数量和金额不会只统计已加载的当前页。
      ["region", "team", "sales", "service", "collect", "sort"].forEach((k) => { if (fv[k]) p[k] = fv[k]; });
      const range = createdRange({ mode: fv.signed, from: fv.sfrom, to: fv.sto });
      if (range) { if (Number.isFinite(range[0])) p.signedFrom = tzDateStr(range[0]); if (Number.isFinite(range[1])) p.signedTo = tzDateStr(range[1] - 1); }
      const { data: d } = await api.get("/crm/signed", { params: p, signal: ctrl.signal });
      if (seq !== reqSeq.current) return;
      setData(d);
      setRows((prev) => (first ? d.rows : [...prev, ...d.rows.filter((r) => !prev.some((x) => x.leadId === r.leadId))]));
      setTotal(d.total || 0);
      setHasMore(!!d.hasMore);
      cursor.current = { offset: d.nextOffset || 0, hasMore: !!d.hasMore };
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      // 接口拒绝访问或网络失败不能显示成“没有客户”；保留明确错误和重试入口，
      // 且丢弃过期请求的错误，避免用户切换筛选后被上一请求覆盖。
      if (seq !== reqSeq.current) return;
      setLoadError(e?.detailText || e.message || "Unable to load");
      if (first) setData(null);
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      if (seq === reqSeq.current) { inflight.current = null; setLoading(false); setLoadingMore(false); }
    }
  }, [qd, cat, dueOnly, fkey]);

  const load = useCallback(() => { cursor.current = { offset: 0, hasMore: false }; setRows([]); fetchPage(0, { fresh: true }); }, [fetchPage]);
  useEffect(() => { cursor.current = { offset: 0, hasMore: false }; setRows([]); fetchPage(0); }, [fetchPage]);
  useEffect(() => () => inflight.current && inflight.current.abort(), []);

  const loadMore = useCallback((force = false) => {
    const now = Date.now();
    if (inflight.current || !cursor.current.hasMore || (!force && now - lastTrigger.current < THROTTLE_MS)) return;
    lastTrigger.current = now;
    fetchPage(cursor.current.offset);
  }, [fetchPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) loadMore(false); }, { rootMargin: `${PREFETCH_PX}px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);
  // 大屏首页未填满（哨兵已在视口内，IO 不会再次触发）→ 页数据变化后补一次检查；只在哨兵真的露出时补页，首屏保持 20 条
  useEffect(() => {
    if (!hasMore || loadingMore) return;
    const el = sentinelRef.current;
    if (el && el.getBoundingClientRect().top < window.innerHeight) loadMore(true);
  }, [rows, hasMore, loadingMore, loadMore]);

  const setCat = (k) => { const p = new URLSearchParams(params); if (k === "all" || cat === k) p.delete("cat"); else p.set("cat", k); setParams(p, { replace: true }); };
  const toggleDue = () => { const p = new URLSearchParams(params); if (dueOnly) p.delete("due"); else p.set("due", "1"); setParams(p, { replace: true }); };
  const counts = data?.counts || {};
  const totals = data?.totals || {};
  const showSales = data?.scope !== "mine";
  const scopeText = data?.scope === "mine" ? (zh ? "我的已签约客户" : "My signed customers") : data?.scope === "region" ? (zh ? "本分公司已签约客户" : "Branch signed customers") : data?.scope === "branches" ? (zh ? "已授权范围的已签约客户" : "Signed customers in your authorized scope") : (zh ? "全部已签约客户" : "All signed customers");

  return (
    <div className="space-y-5" data-testid="signed-customers-page">
      <div className="flex items-center gap-2 flex-wrap">
        <BadgeCheck className="h-6 w-6 text-brand-500" />
        <h1 className="text-2xl font-bold text-slate-900">{zh ? "已签约客户" : "Signed Customers"}</h1>
        <span className="text-xs text-slate-400 hidden sm:inline" data-testid="signed-scope">· {scopeText}</span>
        <div className="flex-wrap md:flex-nowrap ml-auto flex gap-2">
          {canBulk && <Button size="sm" onClick={() => setCompletedOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="signed-completed-open" title={zh ? "按财务数据补录历史完工项目，并登记为已完工已签约客户（与工程部完工项目归档共享同一项目）" : "Backfill completed projects from finance data"}><CheckCircle2 className="h-4 w-4 mr-1" />{zh ? "创建已完工项目" : "Completed projects"}</Button>}
          {canBulk && <Button size="sm" onClick={() => setBulkOpen(true)} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="signed-bulk-open"><FolderInput className="h-4 w-4 mr-1" />{zh ? "从项目创建" : "From projects"}</Button>}
          <Button variant="outline" size="sm" onClick={load} data-testid="signed-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />{zh ? "刷新" : "Refresh"}</Button>
        </div>
      </div>
      {bulkOpen && <SignedBulkCreateDialog api={api} zh={zh} onClose={() => setBulkOpen(false)} onDone={load} />}
      {completedOpen && <CompletedCreateDialog api={api} zh={zh} onClose={() => setCompletedOpen(false)} onDone={load} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="signed-counts">
        {CATS.map((c) => <CountCard key={c.key} c={c} zh={zh} n={c.key === "all" ? counts.total : counts[c.key]} on={cat === c.key && c.key !== "all"} onClick={() => setCat(c.key)} />)}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500 px-1" data-testid="signed-totals">
        <span>{zh ? "合同总额" : "Contracts"} <b className="text-slate-800">{fmtMoney(totals.contractAmount)}</b></span>
        <span>{zh ? "已收" : "Paid"} <b className="text-emerald-700">{fmtMoney(totals.paid)}</b></span>
        <span>{zh ? "未收" : "Unpaid"} <b className="text-slate-800">{fmtMoney(totals.unpaid)}</b></span>
        <button type="button" onClick={toggleDue} data-testid="signed-filter-due" data-on={dueOnly ? 1 : 0} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 transition-colors ${dueOnly ? "bg-red-600 text-white border-red-600" : totals.dueNow > 0 ? "text-red-600 border-red-200 hover:bg-red-50 font-semibold" : "text-slate-500 border-slate-200"}`)} title={zh ? "只看已开票未收款的客户" : "Only invoiced-but-unpaid customers"}>
          <AlertCircle className="h-3.5 w-3.5" />{zh ? "立刻收款" : "Collect now"} <b>{fmtMoney(totals.dueNow)}</b>{totals.dueNowCustomers > 0 && ` · ${totals.dueNowCustomers} ${zh ? "户" : "customers"}`}{dueOnly && <span className="ml-1 opacity-80">✕</span>}
        </button>
      </div>

      {/* 搜索后要直接看到匹配项目：常用筛选放在搜索栏上方，提醒和批量收款移到列表后，
          避免辅助卡片隔开筛选与结果；直接调整渲染顺序，让手机阅读和键盘焦点顺序一致。 */}
      <QuickViews api={api} zh={zh} scope="signed" params={params} setParams={setParams} keys={[...SIGNED_FILTER_KEYS, "cat", "due"]} testid="signed-views" />
      <div className="flex flex-wrap items-center gap-2" data-testid="signed-search-filters">
        <div className="relative w-72">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="signed-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={zh ? "搜索客户 / 地址 / 电话 / 销售 / 项目号" : "Search client / address / phone / sales / project"} className="w-full h-9 pl-9 pr-3 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:border-indigo-400" />
        </div>
        <SignedQuickFilters zh={zh} params={params} setParams={setParams} facets={data?.facets || {}} showRegion={data?.scope === "all" || data?.scope === "branches"} showSales={showSales} count={total} className="contents" />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <ResponsiveTable className="w-full text-sm" data-testid="signed-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5">{zh ? "项目地址" : "Project address"}</th>
              <th className="text-left px-3 py-2.5">{zh ? "分类" : "Stage"}</th>
              {showSales && <th className="text-left px-3 py-2.5">{zh ? "负责销售" : "Sales"}</th>}
              <th className="text-right px-3 py-2.5">{zh ? "合同额" : "Contract"}</th>
              <th className="text-right px-3 py-2.5">{zh ? "已付" : "Paid"}</th>
              <th className="text-right px-3 py-2.5">{zh ? "未付" : "Unpaid"}</th>
              <th className="text-left px-3 py-2.5">{zh ? "收款状态" : "Collection"}</th>
              <th className="text-left px-3 py-2.5">{zh ? "签约日" : "Signed"}</th>
              <th className="px-2 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && rows.length === 0 && <tr><td colSpan={showSales ? 9 : 8} className="p-0"><TableSkeleton rows={6} cols={showSales ? 8 : 7} testId="signed-skeleton" /></td></tr>}
            {!loading && loadError && (
              <tr><td colSpan={showSales ? 9 : 8} className="px-4 py-5">
                <div role="alert" data-testid="signed-list-error" className="space-y-2 text-sm">
                  <p className="font-semibold text-red-700">{zh ? "已签约客户暂时无法加载" : "Signed customers could not load"}</p>
                  <p className="text-slate-600">{loadError}</p>
                  <Button variant="outline" size="sm" onClick={load} data-testid="signed-list-retry">{zh ? "重新加载" : "Retry"}</Button>
                </div>
              </td></tr>
            )}
            {!loading && !loadError && rows.length === 0 && (
              <tr><td colSpan={showSales ? 9 : 8}><EmptyState icon={BadgeCheck} title={zh ? "没有符合条件的已签约客户" : "No signed customers match"} hint={zh ? "调整搜索、施工组等筛选条件后重试" : "Try changing the search, construction team or other filters"} testId="signed-empty" /></td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.leadId} data-testid={`signed-row-${r.leadId}`} data-category={r.category} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => openSigned(r.leadId)}>
                <td className="px-4 py-3">
                  <p className="font-bold text-black" data-testid={`signed-address-${r.leadId}`}>{r.address || r.clientName || r.leadId}{r.city && r.address && !r.address.toLowerCase().includes(r.city.toLowerCase()) ? ` · ${r.city}` : ""}</p>
                  {r.cancelledDesignProject && <p className="mt-1 flex items-start gap-1 text-xs text-amber-700" data-testid={`signed-cancelled-design-${r.leadId}`}>
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>{r.designProjectId
                      ? (zh ? `历史设计项目 ${r.cancelledDesignProject.projectNo || r.cancelledDesignProject.designProjectId} 已取消；当前设计项目 ${r.designNo || r.designProjectId}。` : `Previous design project ${r.cancelledDesignProject.projectNo || r.cancelledDesignProject.designProjectId} was cancelled; current design project ${r.designNo || r.designProjectId}.`)
                      : (zh ? `设计项目 ${r.cancelledDesignProject.projectNo || r.cancelledDesignProject.designProjectId} 已取消。如需继续，可恢复该设计项目，或上传新合同新建设计项目。` : `Design project ${r.cancelledDesignProject.projectNo || r.cancelledDesignProject.designProjectId} was cancelled. To continue, restore it or upload a new contract to create a new design project.`)}</span>
                  </p>}
                  <p className="text-[10px] text-slate-400" data-testid={`signed-sub-${r.leadId}`}>{(SERVICE_LABEL[r.serviceType] || [])[zh ? 0 : 1] || r.serviceType}{r.projectId ? ` · ${r.projectId}` : ""}{r.designNo ? ` · ${r.designNo}` : ""}{r.contractNo ? ` · ${r.contractNo}` : ""}{r.designStatusZh && r.category === "design" ? ` · ${zh ? r.designStatusZh : r.designStatusEn}` : ""}</p>
                </td>
                <td className="px-3 py-3"><CategoryBadge row={r} zh={zh} tid={`signed-cat-${r.leadId}`} /></td>
                {showSales && <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{r.assignedSales || "—"}</td>}
                <td className="px-3 py-3 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums" data-testid={`signed-contract-${r.leadId}`}>{fmtMoney(r.contractAmount)}</td>
                <td className="px-3 py-3 text-right text-emerald-700 whitespace-nowrap tabular-nums" data-testid={`signed-paid-${r.leadId}`}>{fmtMoney(r.paid)}<span className="block text-[10px] text-slate-400">{r.collectedPct}%</span></td>
                <td className="px-3 py-3 text-right text-slate-800 whitespace-nowrap tabular-nums" data-testid={`signed-unpaid-${r.leadId}`}>{fmtMoney(r.unpaid)}</td>
                <td className="px-3 py-3"><DueBadge fin={r} zh={zh} compact tid={`signed-due-${r.leadId}`} /></td>
                <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtD(r.signedDate, "—")}</td>
                <td className="px-2 py-3 text-slate-300"><ChevronRight className="h-4 w-4" /></td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
        <div ref={sentinelRef} data-testid="signed-load-more-sentinel" aria-hidden="true" className="h-px" />
        {rows.length > 0 && (
          <div className="flex-wrap md:flex-nowrap flex items-center gap-4 px-4 py-2.5 border-t border-slate-100 text-[11px] text-slate-500" data-testid="signed-list-footer">
            <span data-testid="signed-loaded-count" data-loaded={rows.length} data-total={total}>{zh ? `已显示 ${rows.length} / ${total}` : `Showing ${rows.length} / ${total}`}</span>
            {loadingMore && <span className="inline-flex items-center gap-1" data-testid="signed-loading-more"><Loader2 className="h-3.5 w-3.5 animate-spin" />{zh ? "加载中…" : "Loading…"}</span>}
            {!loadingMore && hasMore && <button type="button" data-testid="signed-load-more-btn" onClick={() => loadMore(true)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-brand-600 hover:underline font-semibold">{zh ? "加载更多" : "Load more"}</button>}
            {!loadingMore && !hasMore && <span data-testid="signed-list-end">{zh ? "已全部加载" : "All loaded"}</span>}
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400 px-1">{zh ? "分类规则：设计阶段 = 已签约、工程项目尚未开工；施工阶段 = 工程 1–7 阶段进行中；已完工 = City Final 已通过。红色「立刻收款」仅指财务已开票但客户未付款的期次。" : "Design = signed, construction not started; Construction = phases 1–7 in progress; Completed = City Final passed. Red “Collect now” means invoiced but unpaid only."}</p>
      {isMgr && <SignedOverdueDigestCard api={api} zh={zh} onOpenLead={(lid) => openSigned(lid, { collect: "1" })} />}
      {isMgr && <SignedUnreadDigestCard api={api} zh={zh} onOpenLead={(lid) => openSigned(lid)} />}
      {["admin", "ceo", "sales_vp", "sales_manager"].includes(user?.role) && <BatchCollectCard api={api} zh={zh} />}
      {/* 已签约客户详情抽屉：由 URL 的 :id 驱动；内容复用原整页详情组件，功能 100% 保留。
          层级 z-[45] 低于详情页内部弹窗（z-50），保证抽屉里再开弹窗不会被盖住（见 CrmDetailDrawer 注释）。 */}
      {signedId && (
        <CrmDetailDrawer title={zh ? "已签约客户详情" : "Signed customer"} onClose={closeSigned} testId="signed-detail-drawer" flushTop>
          <CRMSignedCustomerHome embedded onClose={closeSigned} />
        </CrmDetailDrawer>
      )}
    </div>
  );
}
